import React, { useContext, useEffect, useState } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { Layout as ALayout, Menu, MenuProps } from 'antd';
import { FaDrawPolygon } from 'react-icons/fa6';
import { LuLayoutDashboard } from 'react-icons/lu';
import { IoSettingsOutline, IoAnalyticsOutline, IoEyeOutline } from 'react-icons/io5';
import { GrCompare } from 'react-icons/gr';
import { BsTools } from 'react-icons/bs';
import { MdBatchPrediction, MdOutlinePhotoSizeSelectActual, MdViewInAr } from 'react-icons/md';
import { Logo, Thumbnail } from './styled';
import iCardioHeartLogo from 'images/icardio-heart.png';
import Header from '../Header/Header';
import { FirebaseUser, IUser } from 'models/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { defaultUser, UserContext } from 'services/contexts';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from 'services/firebase';

const { Content, Footer, Sider } = ALayout;

type MenuItem = Required<MenuProps>['items'][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[]
): MenuItem {
  return {
    key,
    icon,
    children,
    label: <Link to={key as string}>{label}</Link>,
  } as MenuItem;
}

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const rawUser = localStorage.getItem('user');
  const isAuth = localStorage.getItem('isAuthenticated');
  const { setUser, user } = useContext(UserContext);

  useEffect(() => {
    if (isAuth !== JSON.stringify(true)) {
      navigate('/login');
      return;
    }

    if (rawUser != undefined) {
      try {
        const cUser = JSON.parse(rawUser) as IUser;
        setUser(cUser as unknown as FirebaseUser);
      } catch (e) {
        navigate('/login');
      }
    } else {
      navigate('/login');
    }
  }, [isAuth, rawUser]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      if (user) {
        setUser(user as unknown as FirebaseUser);
        // No more /auth/me call here
      } else {
        setUser(defaultUser);
      }
    });

    return () => unsubscribe();
  }, []);

  const showDevShowcase = process.env.REACT_APP_SHOW_DEV_SHOWCASE === 'true';

  const items: MenuItem[] = [
    getItem('Dashboard', '/', <LuLayoutDashboard />),
    getItem(' Segmentation Labels', '/segmentation-labels', <FaDrawPolygon />),
    getItem('Segmentation Predictions', '/segmentation-predictions', <MdBatchPrediction />),
    getItem('Keypoint Labels', '/keypoints', <IoAnalyticsOutline />),
    getItem('View Labels', '/view-labels', <IoEyeOutline />), // Added View Labels
    getItem('Frame Review', '/frame-review', <MdOutlinePhotoSizeSelectActual />),
    getItem('Side-by-Side Segmentations', '/predictions', <GrCompare />),
    getItem('3D Study Viewer', '/3d-study-viewer', <MdViewInAr />),
    user.role == 'admin' ? getItem('Settings', '/settings', <IoSettingsOutline />) : null,
    ...(showDevShowcase ? [getItem('Dev Showcase', '/dev-showcase', <BsTools />)] : []),
  ].filter(Boolean);

  return (
    <ALayout style={{ minHeight: '100vh' }}>
      <Sider collapsible collapsed={collapsed} onCollapse={value => setCollapsed(value)}>
        {collapsed ? (
          <Link to="/">
            <Thumbnail src={iCardioHeartLogo} alt="Logo" />
          </Link>
        ) : (
          <Link to="/">
            <Logo
              src="https://staging.labeler.icardio.ai/static/media/logo.bca2567da2abc5deb5bddebc704bfe00.svg"
              alt="Logo"
            />
          </Link>
        )}
        <Menu
          theme="dark"
          selectedKeys={[
            location.pathname.startsWith('/3d-study-viewer/')
              ? '/3d-study-viewer'
              : location.pathname,
          ]}
          mode="inline"
          items={items}
        />
      </Sider>
      <ALayout>
        <Header />
        <Content>
          <Outlet />
        </Content>
        <Footer style={{ textAlign: 'center', backgroundColor: 'aliceblue', color: '#939090' }}>
          Reviewer ©{new Date().getFullYear()} Created by ICardio.ai
        </Footer>
      </ALayout>
    </ALayout>
  );
};

export default Layout;
